import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import { formatMoney } from '../../utils/format';
import { api } from '../../api';
import TemplatePicker from './TemplatePicker';
import DraftOrderChallenge from './DraftOrderChallenge';
import LineupEditor from './LineupEditor';
import useStatsMode, { ProfileSlot } from './useStatsMode';
import { prefetchProfile } from './PlayerProfile';
import FreeAgents from './FreeAgents';
import PoolSettings from './PoolSettings';
import SeasonView from './SeasonView';
import TradesView from './TradesView';
import usePlayerMeta, { normName, abbrevInjury } from './usePlayerMeta';
import { CountUp, Confetti, TeamAvatar, teamLogo, useFitWidth, fmtCap } from './poolFx';

// stat columns in the team lists: skaters GP · G · A, goalies GP · W · SO · SV%, then NHL · GM · PROJ
const statCols = (pos) => (pos === 'G' ? [['gp', 'GP'], ['w', 'W'], ['so', 'SO'], ['svp', 'SV%']] : [['gp', 'GP'], ['g', 'G'], ['a', 'A']]);
// the Players list mixes skaters and goalies: one column set, '—' where a stat doesn't apply
const ALL_COLS = [['gp', 'GP'], ['g', 'G'], ['a', 'A'], ['w', 'W'], ['so', 'SO'], ['svp', 'SV%']];
const fmtStat = (k, v) => (v == null ? '—' : k === 'svp' ? (v ? Number(v).toFixed(3).replace(/^0/, '') : '—') : v);

export default function PoolLobby({ poolId, userId, onBack, onBalanceChange, onStatusChange, lang = 'en' }) {
  const t = lang === 'fr' ? TR.fr : TR.en;
  const [pool, setPool] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [standings, setStandings] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [activeView, setActiveView] = useState('season');      // season (GM Season pools) | standings | fa | trades | settings
  const [standingsTick, setStandingsTick] = useState(0);       // bump to reload standings (after a pickup)
  // GM Season: trade offers waiting for you (a dot on the Trades tab)
  const [offersWaiting, setOffersWaiting] = useState(0);
  useEffect(() => {
    if (pool?.mode !== 'season' || pool?.status !== 'active') return undefined;
    let alive = true;
    const load = async () => {
      const { count } = await supabase.from('pool_trades').select('id', { count: 'exact', head: true })
        .eq('pool_id', poolId).eq('to_user', userId).eq('status', 'pending');
      if (alive) setOffersWaiting(count || 0);
    };
    load();
    const id = setInterval(load, 45 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, [poolId, userId, pool?.mode, pool?.status, activeView]);
  const fitRef = useFitWidth(1200, [loading]);            // laptops: scale as one piece

  const fetchLobby = useCallback(async () => {
    const { data: poolRow, error: poolErr } = await supabase
      .from('pools').select('*').eq('id', poolId).single();
    if (poolErr) { setError(poolErr.message); setLoading(false); return; }

    const { data: memberRows, error: memErr } = await supabase
      .from('pool_members')
      .select('user_id, draft_slot, score, joined_at, quit_at, profiles(username)')
      .eq('pool_id', poolId)
      .order('draft_slot', { ascending: true });
    if (memErr) { setError(memErr.message); setLoading(false); return; }

    setPool(poolRow);
    onStatusChange?.(poolRow.status); // e.g. lobby → drafting sends you straight to the Draft Room
    setMembers(memberRows || []);
    setLoading(false);
  }, [poolId, onStatusChange]);

  useEffect(() => { fetchLobby(); }, [fetchLobby]);

  useEffect(() => {
    const channel = supabase
      .channel(`pool-lobby-${poolId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pool_members', filter: `pool_id=eq.${poolId}` }, fetchLobby)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pools', filter: `id=eq.${poolId}` }, fetchLobby)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [poolId, fetchLobby]);

  // Fetch standings for active/completed pools
  useEffect(() => {
    if (pool?.status !== 'active' && pool?.status !== 'completed') return;
    let active = true;
    const load = async () => {
      try {
        const data = await api.fetchPoolStandings(poolId);
        if (active) setStandings(data);
      } catch (e) { /* silent */ }
    };
    load();
    const interval = setInterval(load, 60 * 1000);
    return () => { active = false; clearInterval(interval); };
  }, [poolId, pool?.status, standingsTick]);

  const handleJoin = async () => {
    setError(''); setBusy(true);
    const { data, error: rpcErr } = await supabase.rpc('join_pool', {
      p_user_id: userId, p_pool_id: poolId,
    });
    setBusy(false);
    if (rpcErr) { setError(rpcErr.message); return; }
    if (!data?.success) { setError(data?.error || t.errJoin); return; }
    if (data.new_balance !== undefined) onBalanceChange?.(data.new_balance);
    fetchLobby();
  };

  const handleLeave = async () => {
    if (pool?.status === 'active') {
      const ok = window.confirm(t.confirmQuit);
      if (!ok) return;
    }
    setError(''); setBusy(true);
    const { data, error: rpcErr } = await supabase.rpc('leave_pool', {
      p_user_id: userId, p_pool_id: poolId,
    });
    setBusy(false);
    if (rpcErr) { setError(rpcErr.message); return; }
    if (!data?.success) { setError(data?.error || t.errLeave); return; }
    if (data.new_balance !== undefined) onBalanceChange?.(data.new_balance);
    if (data.pool_deleted) { onBack?.(); return; }
    fetchLobby();
  };

  const handleStartDraft = async () => {
    setError(''); setBusy(true);
    // GM Season: CPU GMs fill every empty seat, random order
    const { data, error: rpcErr } = await supabase.rpc(pool?.mode === 'season' ? 'start_season_draft' : 'start_draft', {
      p_user_id: userId, p_pool_id: poolId,
    });
    setBusy(false);
    if (rpcErr) { setError(rpcErr.message); return; }
    if (!data?.success) { setError(data?.error || t.errStart); return; }
    // Realtime will flip pool.status to 'drafting' and PoolsView will route to DraftRoom.
    fetchLobby();
  };

  const handleCancelPool = async () => {
    if (!window.confirm(t.confirmCancel)) return;
    setError(''); setBusy(true);
    const { data, error: rpcErr } = await supabase.rpc('cancel_pool', {
      p_user_id: userId, p_pool_id: poolId,
    });
    setBusy(false);
    if (rpcErr) { setError(rpcErr.message); return; }
    if (!data?.success) { setError(data?.error || t.errCancel); return; }
    onBack?.();
  };

  const handleSettle = async () => {
    if (!window.confirm(t.confirmSettle)) return;
    setError(''); setBusy(true);
    const { data, error: rpcErr } = await supabase.rpc('settle_pool', {
      p_user_id: userId, p_pool_id: poolId,
    });
    setBusy(false);
    if (rpcErr) { setError(rpcErr.message); return; }
    if (!data?.success) { setError(data?.error || t.errSettle); return; }
    if (data.new_balance !== undefined) onBalanceChange?.(data.new_balance);
    fetchLobby();
  };

  if (loading) return <div className="pool-lobby">{t.loading}</div>;
  if (!pool) return <div className="pool-lobby">{error || t.notFound}</div>;

  const myMember = members.find((m) => m.user_id === userId);
  const isMember = !!myMember;
  const hasQuit = !!myMember?.quit_at;
  const isCreator = pool.creator_id === userId;
  const isFull = members.length >= pool.max_members;
  const isSeason = pool.mode === 'season';   // GM Season: full 32-team league, CPU GMs fill the seats
  const canStart = isCreator && pool.status === 'lobby' && (isSeason || members.length >= 2);
  const emptySlots = pool.status === 'lobby'
    ? Array.from({ length: Math.max(0, pool.max_members - members.length) })
    : [];
  const isResults = pool.status === 'active' || pool.status === 'completed';
  const hasViews = pool.status === 'active' && isMember && !hasQuit; // Standings | (My lineup) | (Free agents) | Settings
  // a tab can disappear when the host changes a setting (e.g. lineup mode off) — fall back to Standings
  // GM Season: no Points tab — its Standings tab is the league table
  const view = isSeason && isResults && (!hasViews || activeView === 'settings' || activeView === 'standings') ? 'season'
    : !hasViews || (activeView === 'season' && !isSeason) || (activeView === 'trades' && !isSeason) || activeView === 'lineup' || (activeView === 'fa' && !pool.free_agency) ? 'standings' : activeView;
  const iWon = pool.status === 'completed' && pool.winner_id === userId;
  const fr = lang === 'fr';

  return (
    <div ref={fitRef} className={`pool-lobby pl-status-${pool.status}`}>
      {iWon && <Confetti />}
      <button className="pool-lobby-back" onClick={onBack}>← {t.back}</button>

      {/* ── Hero: name + prize pool ── */}
      <div className="pl-hero">
        <div className="pl-hero-left">
          <div className="pl-hero-badges">
            <span className={`pool-status-badge pool-status-${pool.status}`}>{t[`status_${pool.status}`] || pool.status}</span>
            <span className="pl-format">{pool.format === 'playoff' ? t.formatPlayoff : t.formatRegular}</span>
          </div>
          <h2 className="pool-lobby-title">{pool.name}</h2>
          <div className="pl-hero-facts">
            {!isSeason && <span><b>{formatMoney(pool.entry_fee, lang)}</b> {t.entryFee.toLowerCase()}</span>}
            <span><b>{pool.team_round ? pool.roster_size - 1 : pool.roster_size}</b> {fr ? 'joueurs par équipe' : 'players each'}</span>
            {(pool.slots_f != null || pool.slots_d != null || pool.slots_g != null) && (
              <span><b>{pool.slots_f ?? 0}F · {pool.slots_d ?? 0}D · {pool.slots_g ?? 0}G · {pool.slots_t ?? 1}T</b></span>
            )}
            {pool.cap_mode && pool.cap_amount != null && (
              <span><b>{fmtCap(pool.cap_amount)}</b> {pool.cap_mode === 'hard' ? (fr ? 'plafond strict' : 'hard cap') : (fr ? 'plafond · pénalités' : 'cap · penalties')}</span>
            )}
            <span><b>{pool.pick_seconds >= 60 && pool.pick_seconds % 60 === 0 ? `${pool.pick_seconds / 60} min` : `${pool.pick_seconds}s`}</b> {fr ? 'par choix' : 'per pick'}</span>
          </div>
        </div>
        {isSeason ? (
          <div className="pl-prize">
            <span className="pl-prize-label">{fr ? 'Saison DG' : 'GM Season'}</span>
            <span className="pl-prize-value">{fr ? '32 équipes' : '32 teams'}</span>
            <span className="pl-prize-sub">{fr ? 'La meilleure fiche gagne' : 'Best record wins'}</span>
          </div>
        ) : (
        <div className="pl-prize">
          <span className="pl-prize-label">{fr ? 'Cagnotte' : 'Prize pool'}</span>
          <span className="pl-prize-value"><CountUp value={pool.prize_pool} format={(n) => formatMoney(n, lang)} duration={1200} /></span>
          <span className="pl-prize-sub">{fr ? 'Le gagnant rafle tout' : 'Winner takes all'}</span>
        </div>
        )}
      </div>

      {/* ── Results (active / completed) ── */}
      {pool.status === 'completed' && pool.winner_id && (
        (() => {
          // Champion card: white card, gold accent line, winner's team logo + name, prize, team · points
          const wRow = (standings?.standings || []).find((r) => r.user_id === pool.winner_id);
          const wTeam = (wRow?.roster || []).find((p) => p.position === 'T');
          const wName = wRow?.username || members.find((m) => m.user_id === pool.winner_id)?.profiles?.username || '—';
          return (
            <div className={`pl-champion ${iWon ? 'me' : ''}`}>
              <span className="pl-champion-label">{iWon ? (fr ? 'Tu es champion' : 'You’re the champion') : 'Champion'}</span>
              <span className="pl-champion-who">
                {wTeam ? <TeamAvatar team={{ abbrev: wTeam.team, name: wTeam.player_name }} name={wName} size={40} /> : <TeamAvatar name={wName} size={40} />}
                <span className="pl-champion-name">{wName}</span>
              </span>
              <span className="pl-champion-prize">{formatMoney(pool.prize_pool, lang)}</span>
              {(wTeam || wRow) && (
                <span className="pl-champion-meta">
                  {wTeam?.player_name}{wTeam && wRow ? ' · ' : ''}{wRow ? `${Number(wRow.score).toLocaleString(fr ? 'fr-CA' : 'en-CA')} pts` : ''}
                </span>
              )}
            </div>
          );
        })()
      )}

      {/* lineup / free-agency pools: Standings | My lineup | Free agents */}
      {hasViews && (
        <div className="pl-standings-tabs pl-view-tabs" role="tablist">
          {[
            isSeason && ['season', fr ? 'Ligue' : 'League'],
            !isSeason && ['standings', fr ? 'Ligue' : 'League'],   // standings + every team's lineup (yours is the one you manage)
            pool.free_agency && ['fa', fr ? 'Joueurs autonomes' : 'Free agents'],
            isSeason && ['trades', fr ? 'Échanges' : 'Trades'],
            !isSeason && ['settings', fr ? 'Réglages' : 'Settings'],
          ].filter(Boolean).map(([k, label]) => (
            <button key={k} role="tab" aria-selected={view === k} className={view === k ? 'active' : ''} onClick={() => setActiveView(k)}>{label}{k === 'trades' && offersWaiting > 0 && <span className="ss-badge">{offersWaiting}</span>}</button>
          ))}
        </div>
      )}

      {view === 'trades' && <TradesView pool={pool} userId={userId} lang={lang} standings={standings} onTraded={() => setStandingsTick((n) => n + 1)} />}
      {view === 'season' && <SeasonView poolId={poolId} userId={userId} lang={lang} pool={pool} standings={standings} onOpenTrades={() => setActiveView('trades')}
        playersList={standings && <Standings standings={standings} pool={pool} userId={userId} expandedUser={expandedUser} setExpandedUser={setExpandedUser} t={t} lang={lang} only="players" />} />}
      {view === 'settings' && <PoolSettings pool={pool} userId={userId} lang={lang} onChanged={() => { fetchLobby(); setStandingsTick((n) => n + 1); }} />}
      {view === 'fa' && <FreeAgents pool={pool} userId={userId} lang={lang} onSwapped={() => setStandingsTick((n) => n + 1)} />}

      {isResults && standings && view === 'standings' && (
        <Standings
          standings={standings}
          pool={pool}
          userId={userId}
          expandedUser={expandedUser}
          setExpandedUser={setExpandedUser}
          t={t}
          lang={lang}
        />
      )}

      {/* ── Seats (lobby) / members list ── */}
      {!isResults && (
        <>
          <div className="pl-seats-head">
            <h4 className="pl-seats-title">{fr ? 'Places' : 'Seats'}</h4>
            <span className="pl-seats-count">{members.length}/{pool.max_members} {fr ? 'occupées' : 'filled'}</span>
          </div>
          <div className="pl-seats">
            {members.map((m, i) => {
              const name = m.profiles?.username || '—';
              return (
                <div className={`pl-seat ${m.user_id === userId ? 'me' : ''} ${m.quit_at ? 'quit' : ''}`} key={m.user_id} style={{ '--i': i }}>
                  <span className="pl-seat-slot">#{m.draft_slot}</span>
                  <span className="pl-avatar" aria-hidden="true">{name.slice(0, 2).toUpperCase()}</span>
                  <span className="pl-seat-name">
                    {name}
                    {m.user_id === pool.creator_id && <span className="pl-host">{lang === 'fr' ? 'HÔTE' : 'HOST'}</span>}
                  </span>
                  {m.user_id === userId && <span className="pl-seat-you">{fr ? 'Toi' : 'You'}</span>}
                  {m.quit_at && <span className="pl-seat-you">{t.quitTag}</span>}
                </div>
              );
            })}
            {emptySlots.map((_, i) => (
              <div className="pl-seat empty" key={`empty-${i}`} style={{ '--i': members.length + i }}>
                <span className="pl-seat-slot">#{members.length + i + 1}</span>
                <span className="pl-avatar" aria-hidden="true">+</span>
                <span className="pl-seat-name">{isSeason ? (fr ? 'DG CPU (ou un ami)' : 'CPU GM (or a friend)') : t.emptySlot}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {isMember && pool.status === 'lobby' && !hasQuit && !isSeason && (
        <DraftOrderChallenge poolId={poolId} userId={userId} isCreator={isCreator} lang={lang} />
      )}

      {isMember && pool.status === 'lobby' && !hasQuit && (
        <TemplatePicker poolId={poolId} userId={userId} lang={lang} />
      )}

      {error && <div className="pool-form-error">{error}</div>}

      <div className="pool-lobby-actions">
        {!isMember && pool.status === 'lobby' && (
          <button className="pool-join-btn pl-big" onClick={handleJoin} disabled={busy || isFull}>
            {busy ? '...' : isFull ? t.full : isSeason ? t.join : `${t.join} · ${formatMoney(pool.entry_fee, lang)}`}
          </button>
        )}
        {canStart && (
          <button className="pool-start-btn pl-big" onClick={handleStartDraft} disabled={busy}>
            {busy ? '...' : t.startDraft}
          </button>
        )}
        {isCreator && pool.status === 'lobby' && !canStart && (
          <div className="pl-waiting">{fr ? 'En attente d’au moins 2 joueurs pour lancer le repêchage…' : 'Waiting for at least 2 players to start the draft…'}</div>
        )}
        {isMember && pool.status === 'lobby' && (
          <button className="pool-leave-btn" onClick={handleLeave} disabled={busy}>
            {busy ? '...' : t.leave}
          </button>
        )}
        {isCreator && pool.status === 'lobby' && (
          <button className="pool-cancel-btn" onClick={handleCancelPool} disabled={busy}>
            {busy ? '...' : t.cancelPool}
          </button>
        )}
        {isMember && pool.status === 'active' && !hasQuit && (
          <button className="pool-leave-btn" onClick={handleLeave} disabled={busy}>
            {busy ? '...' : t.quitNoRefund}
          </button>
        )}
        {isCreator && pool.status === 'active' && !isSeason && (
          <button className="pool-start-btn" onClick={handleSettle} disabled={busy}>
            {busy ? '...' : t.settlePool}
          </button>
        )}
        {hasQuit && <div className="pl-waiting">{t.youQuit}</div>}
      </div>
    </div>
  );
}

// Podium (top 3) + two views: Users (team lists) and Players (every drafted player ranked).
// only='players': just the Players list (GM Season's Standings › Players tab)
function Standings({ standings, pool, userId, expandedUser, setExpandedUser, t, lang, only = null }) {
  const [viewState, setView] = useState('users');
  const view = only || viewState;
  const [openAs, setOpenAs] = useState('lineup');
  const listStats = useStatsMode();   // List view: tap a player = his profile under his row (same card as the lineup)
  const { injuryMap } = usePlayerMeta();
  const injTag = (name) => { const st = injuryMap?.get(normName(name || '')); return st ? <span className="dr-tag inj pl-inj" title={st}>{abbrevInjury(st)}</span> : null; };   // lineup pools: an opened team shows its Lineup or its List
  const rows = standings.standings || [];
  const podium = [rows[1], rows[0], rows[2]].filter(Boolean); // 2nd · 1st · 3rd
  // each member's drafted NHL team (position T) is their identity
  const teamOfRow = (r) => { const tm = (r.roster || []).find((p) => p.position === 'T'); return tm ? { abbrev: tm.team, name: tm.player_name } : null; };
  const fr = lang === 'fr';
  const POS = [
    ['F', fr ? 'Attaquants' : 'Forwards'],
    ['D', fr ? 'Défenseurs' : 'Defence'],
    ['G', fr ? 'Gardiens' : 'Goalies'],
  ];
  const logoImg = (abbrev, cls = 'pl-p-logo') => (
    <img className={cls} alt="" src={teamLogo(abbrev)} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
  );

  // GM Season: GM points only matter inside a game (they decide the winner) — the Players list shows real NHL points only
  const nhlOnly = pool.mode === 'season';
  // every drafted player (not the teams) with who drafted him
  const allDrafted = rows.flatMap((r) => (r.roster || [])
    .filter((p) => p.position !== 'T')
    .map((p) => ({ ...p, owner: r, ownerTeam: teamOfRow(r) })))
    .sort((a, b) => (nhlOnly ? (b.nhl_points || 0) - (a.nhl_points || 0) : 0) || (b.points - a.points) || ((a.pick_number || 999) - (b.pick_number || 999)));

  return (
    <div className="pool-standings">
      {!only && <><div className="pl-seats-head">
        <h4 className="pool-standings-title">{t.standings}</h4>
        {standings.last_roster_update && (
          <span className="pool-standings-updated">
            {t.lastUpdated}: {new Date(standings.last_roster_update).toLocaleString(fr ? 'fr-CA' : 'en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </span>
        )}
      </div>

      {rows.length >= 3 && (
        <div className="pl-podium">
          {podium.map((r) => (
            <div key={r.user_id} className={`pl-step rank-${r.rank} ${r.user_id === userId ? 'me' : ''}`}>
              <span className="pl-step-medal">{r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : '🥉'}</span>
              <TeamAvatar team={teamOfRow(r)} name={r.username} size={r.rank === 1 ? 58 : 46} className="pl-avatar" />
              <span className="pl-step-name">{r.username}{r.user_id === pool.creator_id ? ' 👑' : ''}</span>
              <span className="pl-step-score"><CountUp value={r.score} /> <small>{t.pts}</small></span>
              <span className="pl-step-block"><b>{r.rank}</b></span>
            </div>
          ))}
        </div>
      )}

      <div className="pl-standings-tabs" role="tablist">
        {[['users', fr ? 'Joueurs du pool' : 'Users'], ['players', fr ? 'Joueurs LNH' : 'Players']].map(([k, label]) => (
          <button key={k} role="tab" aria-selected={view === k} className={view === k ? 'active' : ''} onClick={() => setView(k)}>{label}</button>
        ))}
      </div></>}

      {view === 'users' && (
        <div className="pool-standings-list">
          {rows.map((entry, i) => {
            const team = teamOfRow(entry);
            const open = expandedUser === entry.user_id;
            return (
              <div
                key={entry.user_id}
                className={`pool-standing-row${entry.rank === 1 ? ' leader' : ''}${entry.quit ? ' quit' : ''}${entry.user_id === userId ? ' me' : ''}${open ? ' open' : ''}`}
                style={{ '--i': i }}
              >
                <div className="pool-standing-summary pl-srow" onClick={() => setExpandedUser(open ? null : entry.user_id)}>
                  <span className="pl-srow-rank">{entry.rank}</span>
                  {team ? logoImg(team.abbrev, 'pl-srow-logo') : <TeamAvatar name={entry.username} size={40} />}
                  <span className="pl-srow-main">
                    <span className="pl-srow-name">
                      {entry.username}
                      {entry.user_id === pool.creator_id && <span className="pl-host">{lang === 'fr' ? 'HÔTE' : 'HOST'}</span>}
                      {entry.user_id === userId && <span className="dr-upnext-tag">{fr ? 'toi' : 'you'}</span>}
                      {entry.quit && <span className="pool-standing-quit">({t.quitTag})</span>}
                    </span>
                    <span className="pl-srow-meta">{team?.name || ''}</span>
                  </span>
                  <span className="pl-srow-score"><b><CountUp value={entry.score} /></b><small>{entry.live_points > 0 ? <span className="pl-live"><i />+{entry.live_points} {fr ? 'en direct' : 'live'}</span> : t.pts}</small></span>
                  <span className="pl-srow-toggle" aria-hidden="true">{open ? '▴' : '▾'}</span>
                </div>

                {open && pool.lineup_mode && (
                  <div className="pl-open-tabs">
                    <div className="pl-standings-tabs" role="tablist">
                      {[['lineup', fr ? 'Alignement' : 'Lineup'], ['list', fr ? 'Liste' : 'List']].map(([k, label]) => (
                        <button key={k} role="tab" aria-selected={openAs === k} className={openAs === k ? 'active' : ''} onClick={() => setOpenAs(k)}>{label}</button>
                      ))}
                    </div>
                  </div>
                )}
                {open && pool.lineup_mode && openAs === 'lineup' && (
                  <div className="pl-open-lineup">
                    {/* your own team = the lineup you manage (drag / tap to move); the others are read-only */}
                    <LineupEditor pool={pool} userId={userId} lang={lang} readOnly={!(entry.user_id === userId && pool.status === 'active' && !entry.quit)} ownerId={entry.user_id} ownerName={entry.username}
                      pointsById={Object.fromEntries((entry.roster || []).map((p) => [p.player_id, p.points]))} />
                  </div>
                )}
                {open && (!pool.lineup_mode || openAs === 'list') && (
                  <div className="pl-teamlist" data-flip-root>
                    {POS.map(([pos, label]) => {
                      const list = (entry.roster || []).filter((p) => p.position === pos)
                        .sort((a, b) => b.points - a.points);
                      if (list.length === 0) return null;
                      const sum = list.reduce((n, p) => n + (p.points || 0), 0);
                      return (
                        <div key={pos} className="pl-teamlist-group">
                          <div className="pl-teamlist-title"><span>{label}</span>{list.some((p) => p.nhl_points != null)
                              ? <span className="pl-tl-cols">
                                  {statCols(pos).map(([k, l]) => <span key={k} className={`pl-st pl-st-${k}`}>{l}</span>)}
                                  <span className="pl-p-nhl">NHL <b>{list.reduce((n, p) => n + (p.nhl_points || 0), 0)}</b></span><span className="pl-p-gm">GM <b>{sum}</b></span>
                                  <span className="pl-st pl-st-proj">{fr ? 'PRÉV' : 'PROJ'}</span>
                                </span>
                              : <span>{sum} {t.pts}</span>}</div>
                          {list.map((p) => (
                            <div key={p.player_id} className="sm-rowwrap">
                              <div role="button" tabIndex={0} className={`pl-p-row pl-p-tap sm-card ${listStats.cls(p.player_id)}`}
                                onClick={listStats.toggle(p.player_id)} onPointerEnter={() => prefetchProfile(p.player_id)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); listStats.toggle(p.player_id)(e); } }}
                                aria-expanded={listStats.statsMode && listStats.profileId === p.player_id}>
                              <span className="pl-p-pick">#{p.pick_number ?? '—'}</span>
                              <span className="dr-slot-pos">{p.position}</span>
                              {logoImg(p.team)}
                              <span className="pl-p-name"><span className="pl-p-nm"><span className="pl-p-nm-text">{p.player_name}</span>{injTag(p.player_name)}</span><small className="pl-p-meta">{p.team}</small></span>
                              <span className="pl-p-pts">{p.live_points > 0 && <i className="pl-live-dot" title={fr ? 'en direct' : 'live'} />}{p.nhl_points != null ? <>{statCols(p.position).map(([k]) => <span key={k} className={`pl-st pl-st-${k}`}>{fmtStat(k, p.stats?.[k])}</span>)}<span className="pl-p-nhl" title={fr ? 'Ses vrais points dans la LNH' : 'His real NHL points'}>{p.nhl_points}</span><span className="pl-p-gm" title={fr ? 'Ce que ça compte dans le pool (×2 / ×0)' : 'What it counts in the pool (×2 / ×0)'}>{p.points}</span><span className="pl-st pl-st-proj" title={fr ? 'Prévision pour la saison (points LNH)' : 'Season projection (NHL points)'}>{p.proj ?? '—'}</span></> : p.points}</span>
                            </div>
                              <ProfileSlot stats={listStats} id={p.player_id} pick={p} lang={lang} poolPoints={p.points} caret="120px" />
                            </div>
                          ))}
                        </div>
                      );
                    })}
                    {team && (
                      <div className="pl-teamlist-group">
                        <div className="pl-teamlist-title"><span>{fr ? 'Équipe' : 'Team'}</span><span>{(entry.roster || []).find((p) => p.position === 'T')?.points ?? 0} {t.pts}</span></div>
                        <div className="pl-p-row">
                          <span className="pl-p-pick">#{(entry.roster || []).find((p) => p.position === 'T')?.pick_number ?? '—'}</span>
                          <span className="dr-slot-pos">{fr ? 'ÉQ' : 'TM'}</span>
                          {logoImg(team.abbrev)}
                          <span className="pl-p-name"><span className="pl-p-nm"><span className="pl-p-nm-text">{team.name}</span></span><small className="pl-p-meta">{fr ? 'points au classement' : 'standings points'}</small></span>
                          <span className="pl-p-pts">{(entry.roster || []).find((p) => p.position === 'T')?.points ?? 0}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {view === 'players' && (
        <div className="pl-players">
          <div className="pl-p-row pl-p-headrow">
            <span className="pl-p-rank">#</span>
            <span className="pl-p-pick">{fr ? 'Choix' : 'Pick'}</span>
            <span />
            <span>{fr ? 'Joueur' : 'Player'}</span>
            <span>{fr ? 'Repêché par' : 'Drafted by'}</span>
            {allDrafted.some((p) => p.nhl_points != null)
              ? <span className="pl-p-pts">{ALL_COLS.map(([k, l]) => <span key={k} className={`pl-st pl-st-${k}`}>{l}</span>)}{nhlOnly ? <span className="pl-p-gm">PTS</span> : <><span className="pl-p-nhl">NHL</span><span className="pl-p-gm">GM</span></>}<span className="pl-st pl-st-proj">{fr ? 'PRÉV' : 'PROJ'}</span></span>
              : <span className="pl-p-pts">{t.pts}</span>}
          </div>
          {allDrafted.map((p, i) => (
            <div key={`${p.owner.user_id}-${p.player_id}`} className={`pl-p-row ${p.owner.user_id === userId ? 'mine' : ''}`}>
              <span className="pl-p-rank">{i + 1}</span>
              <span className="pl-p-pick">#{p.pick_number ?? '—'}</span>
              {logoImg(p.team)}
              <span className="pl-p-name">
                <span className="pl-p-nm"><span className="pl-p-nm-text">{p.player_name}</span>{injTag(p.player_name)}</span>
                <small className="pl-p-meta"><b className="dr-slot-pos">{p.position}</b> {p.team}</small>
              </span>
              <span className="pl-p-owner" data-pick={`· #${p.pick_number ?? '—'}`}>
                <TeamAvatar team={p.ownerTeam} name={p.owner.username} size={24} />
                <span>{p.owner.username}</span>
              </span>
              <span className="pl-p-pts">{p.live_points > 0 && <i className="pl-live-dot" title={fr ? 'en direct' : 'live'} />}{p.nhl_points != null ? <>{ALL_COLS.map(([k]) => <span key={k} className={`pl-st pl-st-${k}`}>{fmtStat(k, p.stats?.[k])}</span>)}{nhlOnly ? <span className="pl-p-gm" title={fr ? 'Ses vrais points dans la LNH' : 'His real NHL points'}>{p.nhl_points}</span> : <><span className="pl-p-nhl" title={fr ? 'Ses vrais points dans la LNH' : 'His real NHL points'}>{p.nhl_points}</span><span className="pl-p-gm" title={fr ? 'Ce que ça compte dans le pool (×2 / ×0)' : 'What it counts in the pool (×2 / ×0)'}>{p.points}</span></>}<span className="pl-st pl-st-proj" title={fr ? 'Prévision pour la saison (points LNH)' : 'Season projection (NHL points)'}>{p.proj ?? '—'}</span></> : p.points}</span>
            </div>
          ))}
          {allDrafted.length === 0 && <div className="pools-empty">{fr ? 'Aucun joueur' : 'No players yet'}</div>}
        </div>
      )}
    </div>
  );
}

const TR = {
  en: {
    back: 'Back to pools',
    loading: 'Loading…',
    notFound: 'Pool not found',
    format: 'Format',
    formatRegular: 'Regular Season',
    formatPlayoff: 'Playoffs',
    entryFee: 'Entry',
    prizePool: 'Prize',
    roster: 'Roster',
    timer: 'Timer',
    members: 'Members',
    emptySlot: 'Open slot',
    join: 'Join Pool',
    leave: 'Leave Pool',
    full: 'Pool is full',
    startDraft: 'Start Draft',
    quitNoRefund: 'Quit Pool (no refund)',
    confirmQuit: 'Quit this pool? Your roster will stay in the competition but you forfeit your entry fee and cannot win.',
    youQuit: 'You quit this pool. Your roster is still scoring toward whoever wins.',
    quitTag: 'quit',
    standings: 'Standings',
    pts: 'pts',
    lastUpdated: 'Stats updated',
    settlePool: 'Settle Pool',
    confirmSettle: 'Settle this pool? The highest-scoring member will win the prize pool.',
    errSettle: 'Could not settle pool',
    winner: 'Winner',
    status_lobby: 'Lobby',
    status_drafting: 'Drafting',
    status_active: 'Active',
    status_completed: 'Completed',
    errJoin: 'Could not join pool',
    errLeave: 'Could not leave pool',
    errStart: 'Could not start the draft',
    cancelPool: 'Cancel Pool',
    confirmCancel: 'Cancel this pool? All entry fees will be refunded and the pool will be deleted.',
    errCancel: 'Could not cancel pool',
  },
  fr: {
    back: 'Retour aux pools',
    loading: 'Chargement…',
    notFound: 'Pool introuvable',
    format: 'Format',
    formatRegular: 'Saison régulière',
    formatPlayoff: 'Séries',
    entryFee: 'Inscription',
    prizePool: 'Cagnotte',
    roster: 'Effectif',
    timer: 'Minuteur',
    members: 'Joueurs',
    emptySlot: 'Place libre',
    join: 'Rejoindre',
    leave: 'Quitter',
    full: 'Pool complet',
    startDraft: 'Lancer le repêchage',
    quitNoRefund: 'Quitter le pool (sans remboursement)',
    confirmQuit: 'Quitter ce pool? Votre alignement restera dans la compétition mais vous perdez votre inscription et ne pouvez plus gagner.',
    youQuit: 'Vous avez quitté ce pool. Votre alignement continue de marquer pour celui qui gagnera.',
    quitTag: 'parti',
    standings: 'Classement',
    pts: 'pts',
    lastUpdated: 'Mis à jour',
    settlePool: 'Conclure le pool',
    confirmSettle: 'Conclure ce pool? Le joueur avec le plus de points remportera la cagnotte.',
    errSettle: 'Impossible de conclure le pool',
    winner: 'Gagnant',
    status_lobby: 'Lobby',
    status_drafting: 'Repêchage',
    status_active: 'Actif',
    status_completed: 'Terminé',
    errJoin: 'Impossible de rejoindre',
    errLeave: 'Impossible de quitter',
    errStart: 'Impossible de lancer le repêchage',
    cancelPool: 'Annuler le pool',
    confirmCancel: "Annuler ce pool? Tous les frais d'inscription seront remboursés et le pool sera supprimé.",
    errCancel: 'Impossible d\'annuler le pool',
  },
};
